import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { OtpInput } from '../components/common/OtpInput'
import { AuthService } from '../services/AuthService'
import { roleStartPath } from '../utils/constants'
import { brand } from '@/ui/brand'

/**
 * Screen A (E-Mail/Passwort) vs. Screen B (OTP-Eingabe), Spec §3.2.
 * `forgot`: E-Mail-Feld für Passwort-Reset, `forgot-sent`: neutrale Bestätigung.
 */
type Stage = 'idle' | 'otp' | 'forgot' | 'forgot-sent'

/** Cooldown für „Code erneut senden" (Spec §3.2). */
const RESEND_COOLDOWN_SECONDS = 30

/** Übersetzt einen fehlgeschlagenen Auth-Request in eine sprechende Fehlermeldung (Spec §3.2). */
function authErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 401) return 'E-Mail/Passwort ungültig'
    if (status === 400) return 'Code ungültig'
    if (status === 429) return 'Zu viele Versuche, bitte später erneut.'
  }
  return 'Etwas ist schiefgelaufen, bitte erneut versuchen.'
}

/**
 * Admin-Login (`/admin/login`, F2 Split): Passwort ist hier immer Pflicht — kein
 * OTP-only-Modus wie im Editor-Login. Screen A (E-Mail + Passwort) → Screen B
 * (OTP), inkl. „Passwort vergessen?"-Reset-Flow und „vertrautes Gerät" (30 Tage).
 * BMC/shadcn-Optik (Light+Dark), Auth-Flow unverändert (Passwort/OTP/Trusted-Device).
 */
export function AdminLoginPage() {
  const { session, status, requestOtp, passwordLogin, verifyOtp } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [stage, setStage] = useState<Stage>('idle')
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // Redirect nach erfolgreichem Login — `from` aus dem RequireAuth-Redirect
  // hat Vorrang vor der rollenrichtigen Startseite.
  useEffect(() => {
    if (status !== 'authed' || !session) return
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname
    navigate(from || roleStartPath(session.role), { replace: true })
  }, [status, session, location.state, navigate])

  // Cooldown-Countdown für den „Code erneut senden"-Button.
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    passwordLogin(email, password)
      .then((result) => {
        if (result.otpRequired) {
          setStage('otp')
          setCooldown(RESEND_COOLDOWN_SECONDS)
        }
        // {token}: Context ist bereits authed (Trusted-Device-Treffer) — Redirect siehe useEffect oben.
      })
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setSubmitting(false))
  }

  const handleVerify = () => {
    setError('')
    setSubmitting(true)
    verifyOtp({ email, code, trustDevice })
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setSubmitting(false))
  }

  const handleResend = () => {
    if (cooldown > 0) return
    setError('')
    requestOtp(email)
      .then(() => setCooldown(RESEND_COOLDOWN_SECONDS))
      .catch((err: unknown) => setError(authErrorMessage(err)))
  }

  const handleCancel = () => {
    setStage('idle')
    setCode('')
    setError('')
  }

  const handleForgotSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    // Backend antwortet immer neutral ({ ok: true }) — kein Existenz-Leak.
    AuthService.requestPasswordReset(email)
      .then(() => setStage('forgot-sent'))
      .catch((err: unknown) => setError(authErrorMessage(err)))
      .finally(() => setSubmitting(false))
  }

  const handleBackToLogin = () => {
    setStage('idle')
    setError('')
  }

  const logo = theme === 'dark' ? brand.logoDark : brand.logoLight

  return (
    <div className="login">
      <div className="login-card">
        <div className="lbrand">
          <img src={logo} alt={brand.name} className="brand-logo" />
        </div>
        <div className="eyebrow">Admin-Konsole</div>
        <h1>
          {brand.name} <span className="text-primary">{brand.product}</span>
        </h1>

        {stage === 'idle' && (
          <form className="loginform" onSubmit={handleSubmit}>
            <div className="field">
              <label className="flabel" htmlFor="login-email">
                E-Mail
              </label>
              <input
                className="inp"
                id="login-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="flabel" htmlFor="login-password">
                Passwort
              </label>
              <input
                className="inp"
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="self-start text-sm font-medium text-primary hover:underline"
                onClick={() => {
                  setError('')
                  setStage('forgot')
                }}
              >
                Passwort vergessen?
              </button>
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button type="submit" className="btn primary block" disabled={submitting || !email || !password}>
              Anmelden
            </button>
          </form>
        )}

        {stage === 'otp' && (
          <div className="loginform">
            <p className="text-sm text-muted-foreground">Code an {email} gesendet.</p>
            <OtpInput value={code} onChange={setCode} />

            <label className="checkrow" htmlFor="trust-device">
              <input
                type="checkbox"
                id="trust-device"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span className="text-muted-foreground">Diesem Gerät 30 Tage vertrauen</span>
            </label>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button
              type="button"
              className="btn primary block"
              disabled={submitting || code.length < 6}
              onClick={handleVerify}
            >
              Bestätigen
            </button>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline disabled:opacity-45"
                disabled={cooldown > 0}
                onClick={handleResend}
              >
                {cooldown > 0 ? `Code erneut senden (${cooldown}s)` : 'Code erneut senden'}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline"
                onClick={handleCancel}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {stage === 'forgot' && (
          <form className="loginform" onSubmit={handleForgotSubmit}>
            <p className="text-sm text-muted-foreground">
              Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum
              Zurücksetzen des Passworts.
            </p>
            <div className="field">
              <label className="flabel" htmlFor="forgot-email">
                E-Mail
              </label>
              <input
                className="inp"
                id="forgot-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <button type="submit" className="btn primary block" disabled={submitting || !email}>
              Link senden
            </button>
            <button
              type="button"
              className="self-start text-sm font-medium text-primary hover:underline"
              onClick={handleBackToLogin}
            >
              Zurück zum Login
            </button>
          </form>
        )}

        {stage === 'forgot-sent' && (
          <div className="loginform">
            <p className="text-sm text-muted-foreground">
              Falls ein Konto existiert, wurde eine E-Mail gesendet.
            </p>
            <button type="button" className="btn primary block" onClick={handleBackToLogin}>
              Zurück zum Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
